import type { TaskCard, TaskContextType, TaskKind, TaskSize } from '@shared/types/tasks';
import { parseTaskNumber } from '@shared/task-number';
import { isSnoozed } from '@/lib/snooze';

/**
 * Board filters live in the URL (so Saved views work; `TASKS_URL` in board-url.ts): mine, following, assignee, label, tag, type, q, archived, snoozed.
 * `assignee=me` means whoever is looking; `assignee=none` and `type=none` match empty.
 */
export interface BoardFilters {
  /** My tasks: assigned to me, created by me with no one assigned, or waiting on me (T-331). */
  mine: boolean;
  /** Following (T-295): tasks I follow that aren't assigned to me. `mine` wins when both are set. */
  following: boolean;
  assignee: string[];
  label: string[];
  /** Task types (T-331), by id. */
  taskType: string[];
  /** Sizes and kinds (T-331); `none` is a task without one. */
  size: Array<TaskSize | 'none'>;
  kind: Array<TaskKind | 'none'>;
  /** `#tags`, without the #. */
  tag: string[];
  type: Array<TaskContextType | 'none'>;
  q: string;
  archived: boolean;
  /** Include snoozed tasks (T-50); they're off the Board until their snooze ends. */
  snoozed: boolean;
  /** Show AI summaries inside the cards (T-318): how cards look, so not counted as a filter. */
  summaries: boolean;
}

export const EMPTY_FILTERS: BoardFilters = { mine: false, following: false, assignee: [], label: [], taskType: [], size: [], kind: [], tag: [], type: [], q: '', archived: false, snoozed: false, summaries: false };

export function hasActiveFilters(filters: BoardFilters): boolean {
  return Boolean(filters.mine || filters.following || filters.assignee.length || filters.label.length || filters.taskType.length || filters.size.length || filters.kind.length || filters.tag.length || filters.type.length || filters.q.trim() || filters.archived || filters.snoozed);
}

/** The Board's My tasks / Following / All choice, kept in the URL as `mine` or `following`. */
export type BoardScope = 'mine' | 'following' | 'all';

export function boardScope(filters: BoardFilters): BoardScope {
  return filters.mine ? 'mine' : filters.following ? 'following' : 'all';
}

export function withBoardScope(filters: BoardFilters, scope: BoardScope): BoardFilters {
  return { ...filters, mine: scope === 'mine', following: scope === 'following' };
}

export function filterTasks(tasks: TaskCard[], filters: BoardFilters, meId: string | undefined): TaskCard[] {
  const assignees = new Set(filters.assignee.map((a) => (a === 'me' ? meId ?? '' : a)));
  const q = filters.q.trim().toLowerCase();
  // "T-14", "t14" or "14" also finds that task by its number, as the top-bar search does.
  const number = parseTaskNumber(q);
  return tasks.filter((task) => {
    if (!filters.archived && task.archived) return false;
    if (!filters.snoozed && isSnoozed(task.snoozed_until)) return false;
    if (filters.mine) {
      const assignedToMe = Boolean(meId) && task.assignee_ids.includes(meId!);
      const mineUnassigned = Boolean(meId) && task.created_by_user_id === meId && task.assignee_ids.length === 0;
      // Waiting on me (T-331): my turn, whoever owns it.
      const waitingOnMe = Boolean(meId) && task.waiting_on.some((w) => w.target === 'user' && w.user_id === meId);
      if (!assignedToMe && !mineUnassigned && !waitingOnMe) return false;
    } else if (filters.following) {
      if (!meId || !task.follower_ids.includes(meId) || task.assignee_ids.includes(meId)) return false;
    }
    if (assignees.size) {
      const matches =
        (assignees.has('none') && task.assignee_ids.length === 0) || task.assignee_ids.some((id) => assignees.has(id));
      if (!matches) return false;
    }
    if (filters.label.length && !task.label_ids.some((id) => filters.label.includes(id))) return false;
    if (filters.taskType.length && !filters.taskType.includes(task.type_id)) return false;
    if (filters.size.length && !filters.size.includes(task.size ?? 'none')) return false;
    if (filters.kind.length && !filters.kind.includes(task.kind ?? 'none')) return false;
    if (filters.tag.length && !task.tags.some((t) => filters.tag.includes(t))) return false;
    if (filters.type.length) {
      const matches =
        (filters.type.includes('none') && task.contexts.length === 0) ||
        task.contexts.some((c) => filters.type.includes(c.type));
      if (!matches) return false;
    }
    if (q) {
      const haystack = [task.title, ...task.contexts.map((c) => c.title), ...task.tags.map((t) => `#${t}`)].join(' ').toLowerCase();
      if (task.number !== number && !haystack.includes(q)) return false;
    }
    return true;
  });
}
